import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ar", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
};

export default function UsersCard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // username pending confirmation

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data.users || []);
    } catch (e) {
      /* non-owner or error: keep empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const addUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      await api.post("/users", { username: username.trim(), password });
      setSuccess(`تم إنشاء المستخدم ${username.trim()} بنجاح`);
      setUsername("");
      setPassword("");
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.detail || "تعذر إنشاء المستخدم");
    }
    setSaving(false);
  };

  const deleteUser = async (uname) => {
    if (confirmDelete !== uname) {
      setConfirmDelete(uname);
      setTimeout(() => setConfirmDelete((c) => (c === uname ? null : c)), 4000);
      return;
    }
    setConfirmDelete(null);
    setError(null);
    setSuccess(null);
    try {
      await api.delete(`/users/${encodeURIComponent(uname)}`);
      setSuccess(`تم حذف ${uname}`);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  return (
    <section className="card users-card" data-testid="owner-user-management-card">
      <div className="card-head">
        <h2>👥 إدارة المستخدمين</h2>
        <span className="owner-chip" data-testid="owner-chip">🔒 خاص بالمالك</span>
      </div>
      <p className="tickets-sub">
        أضف مستخدمين جدد للوصول للوحة والتطبيق — فقط حسابك (المالك) يرى هذه القائمة ويديرها
      </p>

      <form onSubmit={addUser} className="users-add-form" data-testid="user-management-add-form">
        <input
          className="field-input"
          placeholder="اسم المستخدم الجديد"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          minLength={3}
          autoComplete="off"
          data-testid="user-management-add-username-input"
        />
        <input
          className="field-input"
          type="password"
          placeholder="كلمة المرور"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={4}
          autoComplete="new-password"
          data-testid="user-management-add-password-input"
        />
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="user-management-add-submit-button">
          {saving ? <span className="spinner" /> : "➕"} إضافة مستخدم
        </button>
      </form>

      {error && <div className="login-error" data-testid="user-management-error">✖ {error}</div>}
      {success && <div className="settings-success" data-testid="user-management-success">✔ {success}</div>}

      {loading ? (
        <div className="monitor-loading"><span className="spinner big" /> جاري التحميل...</div>
      ) : (
        <div className="users-list" data-testid="user-management-users-list">
          {users.map((u) => (
            <div key={u.username} className="user-row" data-testid={`user-row-${u.username}`}>
              <span className="user-row-name">
                👤 {u.username}
                {u.is_owner && <span className="owner-badge" data-testid="user-owner-badge">المالك 🛡</span>}
              </span>
              <span className="user-row-date">{fmtDate(u.created_at)}</span>
              {u.is_owner ? (
                <span className="user-protected" data-testid="user-protected-label">محمي</span>
              ) : (
                <button
                  className={`btn btn-sm ${confirmDelete === u.username ? "btn-danger" : "btn-ghost"}`}
                  onClick={() => deleteUser(u.username)}
                  data-testid="user-management-delete-user-button"
                >
                  {confirmDelete === u.username ? "تأكيد الحذف؟" : "🗑 حذف"}
                </button>
              )}
            </div>
          ))}
          {users.length === 0 && <div className="tickets-empty">لا يوجد مستخدمون</div>}
        </div>
      )}
    </section>
  );
}
